import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Cpu, DollarSign, Activity, Save, Loader2 } from "lucide-react";

const MODEL_OPTIONS = ["claude-haiku-4-5", "claude-sonnet-5", "claude-opus-4-1"];
const fmt = (n) => (n || 0).toLocaleString();
const usd = (n) => `$${(n || 0).toFixed(2)}`;

const WINDOWS = [
  { key: "today", label: "Today (24h)" },
  { key: "week", label: "Last 7 days" },
  { key: "month", label: "Last 30 days" },
];

export default function AiSpend() {
  const [data, setData] = useState(null);
  const [light, setLight] = useState("");
  const [heavy, setHeavy] = useState("");
  const [alert, setAlert] = useState(0);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/ai-spend").then((r) => {
    setData(r.data); setLight(r.data.model_light); setHeavy(r.data.model_heavy); setAlert(r.data.daily_cost_alert || 0);
  }).catch(() => toast.error("Could not load AI spend"));

  useEffect(() => { load(); }, []);

  const saveModels = async () => {
    setSaving(true);
    try {
      const { data: d } = await api.put("/admin/ai-settings", { model_light: light, model_heavy: heavy, daily_cost_alert: Number(alert) || 0 });
      setLight(d.model_light); setHeavy(d.model_heavy); setAlert(d.daily_cost_alert || 0);
      toast.success("AI settings saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save settings"); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid="ai-spend-page" className="mx-auto max-w-5xl px-6 py-8">
      <div className="flex items-center gap-2 mb-1">
        <Cpu className="h-5 w-5 text-teal" />
        <h1 className="text-2xl font-bold text-slate-900">AI Spend & Models</h1>
      </div>
      <p className="text-sm text-slate-500 mb-6">Claude token usage and estimated cost across all orders (uses list prices — your Anthropic invoice is authoritative).</p>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        {WINDOWS.map((w) => {
          const d = data?.windows?.[w.key] || {};
          return (
            <div key={w.key} data-testid={`spend-${w.key}`} className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">{w.label}</div>
              <div className="mt-2 flex items-baseline gap-1">
                <DollarSign className="h-4 w-4 text-emerald-600" />
                <span className="text-3xl font-bold text-slate-900">{usd(d.cost)}</span>
              </div>
              <div className="mt-3 space-y-1 text-xs text-slate-500">
                <div className="flex justify-between"><span><Activity className="inline h-3 w-3 mr-1" />Calls</span><span className="font-semibold text-slate-700">{fmt(d.calls)}</span></div>
                <div className="flex justify-between"><span>Input tokens</span><span className="font-semibold text-slate-700">{fmt(d.input)}</span></div>
                <div className="flex justify-between"><span>Output tokens</span><span className="font-semibold text-slate-700">{fmt(d.output)}</span></div>
                <div className="flex justify-between border-t border-slate-100 pt-1"><span>Total tokens</span><span className="font-semibold text-slate-900">{fmt((d.input || 0) + (d.output || 0))}</span></div>
              </div>
              {d.by_model && Object.keys(d.by_model).length > 0 && (
                <div className="mt-3 border-t border-slate-100 pt-2 space-y-1">
                  {Object.entries(d.by_model).map(([m, b]) => (
                    <div key={m} className="flex justify-between text-[11px] text-slate-400">
                      <span className="truncate mr-2">{m}</span><span>{fmt(b.calls)} · {usd(b.cost)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
        <h2 className="text-lg font-bold text-slate-900 mb-1">Model Tuning</h2>
        <p className="text-sm text-slate-500 mb-5">Switch models without redeploying. Light model runs order parsing + summaries; heavy model runs deed & report extraction.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Light model (parsing / summaries)</label>
            <select data-testid="model-light-select" value={light} onChange={(e) => setLight(e.target.value)}
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-teal focus:outline-none">
              {[...new Set([light, ...MODEL_OPTIONS])].filter(Boolean).map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Heavy model (deed / report)</label>
            <select data-testid="model-heavy-select" value={heavy} onChange={(e) => setHeavy(e.target.value)}
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-teal focus:outline-none">
              {[...new Set([heavy, ...MODEL_OPTIONS])].filter(Boolean).map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
        </div>
        <div className="mt-5 max-w-xs">
          <label className="block text-xs font-semibold text-slate-600 mb-1">Daily cost alert threshold (USD)</label>
          <input data-testid="daily-alert-input" type="number" min="0" step="1" value={alert} onChange={(e) => setAlert(e.target.value)}
            placeholder="0 = off" className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-teal focus:outline-none" />
          <p className="mt-1 text-[11px] text-slate-400">Emails all admins once/day when today's estimated cost crosses this amount (0 disables).</p>
        </div>
        <button data-testid="save-models-btn" onClick={saveModels} disabled={saving}
          className="mt-5 inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-navy-2 transition-colors disabled:opacity-50">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save models
        </button>
      </div>
    </div>
  );
}
